import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { ResultActions } from "@/components/ResultActions";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wrench, Loader2, Zap, Star } from "lucide-react";

const PLAYSTYLES = [
  { v: "Aggressive", d: "Pressure & downs" },
  { v: "Stealth", d: "Stay unseen" },
  { v: "Chase", d: "Loop & outplay" },
  { v: "Support", d: "Help your team" },
  { v: "Meme", d: "Pure chaos" },
];

export default function BuildGenerator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("Killer");
  const [character, setCharacter] = useState("Random");
  const [playstyle, setPlaystyle] = useState("Aggressive");
  const [loading, setLoading] = useState(false);
  const [build, setBuild] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [chars, setChars] = useState({ killers: [], survivors: [] });

  useEffect(() => {
    api.get("/dbd/data").then(({ data }) => setChars({ killers: data.killers, survivors: data.survivors })).catch(() => {});
  }, []);

  const charList = role === "Killer" ? chars.killers : chars.survivors;

  const generate = async () => {
    if (!user) return setAuthOpen(true);
    setLoading(true);
    setBuild(null);
    try {
      const body = { role, playstyle };
      if (character !== "Random") body.character = character;
      const { data } = await api.post("/generate/build", body);
      setBuild(data);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      } else {
        toast.error("Generation failed, please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="build-gen-page" className="mx-auto max-w-5xl px-6 py-14 md:px-10">
      <div className="mb-3 flex items-center gap-2 text-primary">
        <Wrench className="h-5 w-5" />
        <span className="text-xs font-bold uppercase tracking-[0.25em]">Build Generator</span>
      </div>
      <h1 className="font-display text-5xl tracking-wider md:text-6xl">Craft the perfect <span className="text-primary">loadout</span></h1>
      <p className="mt-3 max-w-2xl text-foreground/60">Pick a role, character and playstyle for a synergistic 4-perk build. <span className="text-primary/80">Free &amp; unlimited — no daily limit.</span></p>

      <div className="mt-8 grid gap-5 border border-border bg-card p-6 md:p-8">
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">Role</p>
          <div className="flex gap-2.5">
            {["Killer", "Survivor"].map((r) => (
              <button key={r} data-testid={`build-role-${r}`} onClick={() => { setRole(r); setCharacter("Random"); }} className={`flex-1 border px-4 py-3 text-sm font-bold uppercase tracking-widest transition-all md:flex-none md:px-10 ${role === r ? "border-primary bg-primary text-primary-foreground" : "border-white/10 text-foreground/70 hover:border-primary/50"}`}>
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">Character</p>
            <Select value={character} onValueChange={setCharacter}>
              <SelectTrigger data-testid="build-character-select" className="rounded-sm border-white/10 bg-background"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72 border-white/10 bg-card">
                <SelectItem value="Random">🎲 Random</SelectItem>
                {charList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">Playstyle</p>
            <Select value={playstyle} onValueChange={setPlaystyle}>
              <SelectTrigger data-testid="build-playstyle-select" className="rounded-sm border-white/10 bg-background"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-card">
                {PLAYSTYLES.map((p) => <SelectItem key={p.v} value={p.v}>{p.v} — {p.d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button data-testid="generate-build-btn" onClick={generate} disabled={loading} className="rounded-sm bg-primary py-6 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 md:w-auto md:px-10">
          {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Assembling…</> : <><Zap className="mr-2 h-5 w-5" /> Generate Build</>}
        </Button>
      </div>

      {build && (
        <div data-testid="build-result" className="fade-up mt-6 border border-primary/30 bg-card p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{build.role}</span>
            <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{build.character}</span>
            <span className="border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">{build.playstyle}</span>
          </div>
          <div className="mt-4 flex items-end justify-between gap-4">
            <h2 className="font-display text-3xl tracking-wide md:text-4xl">{build.build_name}</h2>
            {build.strength_rating != null && (
              <div className="flex shrink-0 items-center gap-1.5 text-primary">
                <Star className="h-5 w-5 fill-primary" />
                <span className="font-display text-2xl">{build.strength_rating}/10</span>
              </div>
            )}
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {Array.isArray(build.perks) && build.perks.map((p, i) => (
              <div key={p.name || i} className="border border-white/10 bg-background/50 p-4 transition-colors hover:border-primary/40">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                  <p className="font-bold text-foreground">{p.name}</p>
                </div>
                <p className="mt-2 text-sm text-foreground/60">{p.reason}</p>
              </div>
            ))}
          </div>

          {build.strategy && (
            <div className="mt-6 border-l-2 border-primary bg-background/40 p-4">
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-primary">Strategy</p>
              <p className="text-sm text-foreground/70">{build.strategy}</p>
            </div>
          )}

          <div className="mt-7 border-t border-white/10 pt-5">
            <ResultActions kind="build" data={build} onRequireAuth={() => setAuthOpen(true)} />
          </div>
        </div>
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
